import React, {
  useState,
  useEffect,
  useMemo,
  useImperativeHandle,
  forwardRef,
  useRef,
} from 'react';
import axios from 'axios';
import moment from 'moment';
import configData from '../../config.json';
import './OrderInformation.css';
import ToggleNav from '../../components/ToggleNav';
import PhoneInput from 'react-phone-input-2';
import DatePicker from 'react-datepicker';
import TimePicker from '../ui/TimePicker';
import 'react-datepicker/dist/react-datepicker.css';
import 'react-time-picker/dist/TimePicker.css';
import { GenericSelect } from '../../components/GenericSelect';
import { minMaxInputNumber } from '../../utils';
import { toast } from 'react-toastify';
import { useDataContext } from '../../context/data.js';
import { languageToPhoneCountry } from '../../utils/language';
import translations from '../../constants/translations.json';
import { useLagTime } from '../../hooks/useLagTime.js';
import CinemaRowSeatDropdowns from './CinemaRowSeatDropdowns';
import AddressInformation from './AddressInformation';
import {
  formatTheaterDayHeading,
  formatTheaterPerformanceParts,
  groupTheaterPerformancesByDay,
  THEATER_VISIBLE_DAYS,
} from '../../utils/theater';
import DiscountCodeBlock from './DiscountCodeBlock';

const CINEMA_ROW_SEAT_RESTAURANT_ID = '699330a16d92940019189433';

// A stable "no minimum to worry about", so an omitted prop is not a new object
// identity on every render.
const EMPTY_WOLT_MIN_ORDER = { met: true, message: '' };

const OrderInformation = forwardRef(
  (
    {
      restaurant,
      restaurantId: restaurantIdProp,
      orderType,
      orderTypeOptions,
      onOrderTypeChange,
      tableNumber = '1',
      onTableNumberChange,
      tipId = null,
      tipPercentage = '0',
      onTipPercentageChange,
      phoneNumber = '',
      onPhoneNumberChange,
      comments = '',
      onCommentsChange,
      deliveryComment = '',
      onDeliveryCommentChange,
      discountCode = '',
      onDiscountCodeChange,
      onApplyDiscount,
      // Guest discount code. The page owns every one of these: it holds the
      // state, it runs the debounced lookup itself (useDiscountCodeLookup — the
      // lookup cannot live down here, because the review step unmounts on the
      // way to payment), and the SERVER owns the amount. This block only draws it.
      onRemoveDiscount,
      discountLocked = false,
      discountStatus = 'idle',
      discountAmount = 0,
      discountError = '',
      currency = 'DKK',
      baseAmount = 1,
      isValidPhone,
      setIsValidPhone,
      pickup,
      pickupOptions,
      onPickupChange,
      pickupDate = new Date(),
      onPickupDateChange,
      pickupTime = '12:00',
      onPickupTimeChange,
      isPickupForTomorrow,
      disabledTimes = null,
      pickupTimeInterval = 10,
      // Theater mode: upcoming performances + the chosen one. The pickup
      // now/later toggle and date/time picker are replaced by this picker when
      // the guest is on the pre-order flow.
      theaterPerformances = [],
      theaterPerformanceId = null,
      onTheaterPerformanceChange,
      // Did the guest pick the pre-order tile? A theater venue also sells for
      // now, and both flows arrive here as take-away — so the venue's
      // TheaterModeEnabled flag can no longer stand in for this.
      isTheaterPreorder = false,
      // The other theater flow: an immediate order at a theater. It asks for a
      // performance too, but only as context for the kitchen — a theater has no
      // tables, so this is what identifies the guest in place of a table number.
      isTheaterOrderNow = false,
      roomNumber = '',
      onRoomNumberChange = '',
      hasBag = false,
      onBagToggle,
      bagFee,
      hasDeliveryFee = false,
      onDeliveryFeeToggle,
      deliveryFee,
      woltDelivery,
      setWoltDelivery,
      setWoltDeliveryFee,
      onWoltDeliveryFeeToggle,
      woltDeliveryFee,
      // The venue's delivery minimum against the current cart, decided by the
      // page (utils/woltMinOrder) so this block and the checkout gate read the
      // same verdict. Defaults to "met", so nothing shows for a venue without one.
      woltMinOrder = EMPTY_WOLT_MIN_ORDER,
      onCheckWoltDeliveryBlur,
      addressRef,
      address,
      setAddress,
      // Drops the page's Wolt quote the moment the address is edited.
      onWoltAddressEdit,
      tableNumbers,
      restaurantTips,
      isGuest,
      guestId,
      fromHeyCharity,
      fromKiosk = false,
      isParkTable = false,
    },
    ref,
  ) => {
    let pathname = window.location.pathname;
    // Prefer the id the page already resolved; fall back to the URL for callers
    // that don't pass it (the kiosk page), so behaviour there is unchanged.
    const restaurantId = restaurantIdProp || pathname.split('/')?.[2];
    const { language } = useDataContext();
    const { lagTime } = useLagTime();

    // The pre-order flow replaces the pickup now/later toggle + date/time
    // picker: the guest picks a performance instead and the backend derives
    // pickup timing from it. Kiosk, dine-in and ordinary take-away — including
    // take-away at a theater — stay untouched.
    const isTheaterMode = Boolean(isTheaterPreorder) && !fromKiosk && orderType === 'take-away';

    // Both theater flows ask which performance the guest is here for; only the
    // pre-order lets that answer decide WHEN the order happens.
    const showTheaterPicker = isTheaterMode || Boolean(isTheaterOrderNow);

    // Form validation states
    const [tableNumberError, setTableNumberError] = useState(false);
    const [phoneError, setPhoneError] = useState(false);
    const [pickupDateError, setPickupDateError] = useState(false);
    const [pickupTimeAvailabilityError, setPickupTimeAvailabilityError] = useState(false);
    const [pickupTimeError, setPickupTimeError] = useState(false);
    const [pickupTimeCapacityError, setPickupTimeCapacityError] = useState(false);
    const [roomNumberError, setRoomNumberError] = useState(false);
    const [theaterPerformanceError, setTheaterPerformanceError] = useState(false);
    // Theater mode: the picker opens on the nearest days only — a season can be
    // a year long — and this reveals the rest of the program.
    const [showAllTheaterDays, setShowAllTheaterDays] = useState(false);
    const [formTouched, setFormTouched] = useState(false);
    // Take-away and delivery both run on a chosen slot when ordering for later,
    // and both are held to the same date, time and capacity rules — a delivery
    // for a closed or full slot is as unservable as a pickup for one.
    const hasAdvanceSlot = orderType === 'take-away' || orderType === 'delivery';
    const isDelivery = orderType === 'delivery';

    // Capacity-management slot availability for the picked date. Empty when
    // capacity is disabled on the restaurant or the fetch fails — fail open.
    const [capacitySlots, setCapacitySlots] = useState([]);

    useEffect(() => {
      if (!restaurantId || !pickupDate) return;
      const date = moment(pickupDate).format('MM/DD/YYYY');
      let cancelled = false;
      axios
        .get(`${configData.SERVER_URL}/capacity/slots`, {
          params: { restaurantId, date },
        })
        .then((res) => {
          if (cancelled) return;
          setCapacitySlots(res.data && res.data.enabled ? res.data.slots || [] : []);
        })
        .catch(() => {
          if (!cancelled) setCapacitySlots([]);
        });
      return () => {
        cancelled = true;
      };
    }, [restaurantId, pickupDate]);

    // Re-evaluate capacity error whenever slot data changes — surfaces the
    // inline warning immediately if the user's pre-picked time became full
    // while the form was open. Hook-gated: skipped when no slots loaded.
    useEffect(() => {
      if (!capacitySlots.length || !hasAdvanceSlot || pickup !== 'later') return;
      setPickupTimeCapacityError(pickupTime && isCapacityFull(pickupTime));
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [capacitySlots, pickupTime, orderType, pickup]);

    // Refs for scrolling to error fields
    const tableNumberRef = useRef(null);
    const phoneRef = useRef(null);
    const pickupDateTimeRef = useRef(null);
    const roomNumberRef = useRef(null);

    const bagOptions = useMemo(
      () => [
        {
          id: 'yes',
          label: `${translations[language]?.order_information_bag_option_yes} + ${currency} ${bagFee}`,
        },
        { id: 'no', label: translations[language]?.order_information_bag_option_no },
      ],
      [language, currency, bagFee],
    );

    const tipAmount = ((parseFloat(tipPercentage) * baseAmount) / 10).toFixed(2);

    // Validate table number
    const validateTableNumber = (value) => {
      if (orderType === 'dine-in' && (!value || value === '')) {
        setTableNumberError(true);

        if (restaurantId !== CINEMA_ROW_SEAT_RESTAURANT_ID) {
          toast.error(translations[language]?.order_information_table_number_error);
        }

        return false;
      }
      setTableNumberError(false);
      return true;
    };

    // Validate phone number
    const validatePhone = (value) => {
      // Basic phone validation - at least 8 digits
      const isValid = value && value.replace(/\D/g, '').length >= 8;
      setPhoneError(!isValid);
      setIsValidPhone?.(isValid);
      return isValid;
    };

    // Validate pickup date
    const validatePickupDate = (date) => {
      if (hasAdvanceSlot) {
        const today = new Date();
        today.setHours(0, 0, 0, 0);

        if (!date || date < today) {
          setPickupDateError(true);
          toast.error(
            translations[language]?.[
              isDelivery
                ? 'order_information_deliver_later_date_error'
                : 'order_information_pickup_later_date_error'
            ],
          );
          return false;
        }
      }
      setPickupDateError(false);
      return true;
    };

    const pickupTimeErrorKey = isDelivery
      ? 'order_information_deliver_later_time_error'
      : 'order_information_pickup_later_time_error';

    // Validate pickup time
    const validatePickupTime = (time) => {
      if (hasAdvanceSlot && pickup === 'later') {
        if (
          arraysEqual(
            timeOptions.map((item) => item.value),
            timeOptions.filter((item) => item.disabled).map((item) => item.value),
          )
        ) {
          setPickupTimeAvailabilityError(true);
          toast.error(
            translations[language]?.[
              isDelivery
                ? 'order_information_deliver_later_time_unavailable'
                : 'order_information_pickup_later_time_unavailable'
            ],
          );
          return false;
        }

        if (!time || time === '') {
          setPickupTimeError(true);
          toast.error(translations[language]?.[pickupTimeErrorKey]);
          return false;
        }
      }

      // If pickup is for today, validate that time is not in the past
      if (hasAdvanceSlot && pickup === 'later' && pickupDate) {
        const today = new Date();
        const selectedDate = new Date(pickupDate);

        if (selectedDate.setHours(0, 0, 0, 0) === today.setHours(0, 0, 0, 0)) {
          const now = new Date();
          const [hours, minutes] = time.split(':').map(Number);
          const selectedTime = new Date();
          selectedTime.setHours(hours, minutes, 0, 0);

          if (selectedTime < now) {
            setPickupTimeError(true);
            toast.error(translations[language]?.[pickupTimeErrorKey]);
            return false;
          }
        }
      }

      // Capacity-management: block submit if selected time falls in a full slot.
      // Hook-gated on capacitySlots (empty when capacity off / fetch failed).
      if (hasAdvanceSlot && pickup === 'later' && time && isCapacityFull(time)) {
        setPickupTimeCapacityError(true);
        setPickupTimeError(false);
        setPickupTimeAvailabilityError(false);
        return false;
      }

      setPickupTimeAvailabilityError(false);
      setPickupTimeError(false);
      setPickupTimeCapacityError(false);
      return true;
    };

    // Theater mode: the program grouped into days, and the slice of it the
    // picker shows before the guest asks for more. A venue can publish a whole
    // season, so opening on ~a week of dates keeps the choice readable while
    // the rest stays one tap away.
    const theaterDays = useMemo(
      () => groupTheaterPerformancesByDay(theaterPerformances),
      [theaterPerformances],
    );
    const visibleTheaterDays = showAllTheaterDays
      ? theaterDays
      : theaterDays.slice(0, THEATER_VISIBLE_DAYS);
    const hiddenTheaterDayCount = theaterDays.length - visibleTheaterDays.length;

    // A performance already chosen further out — the guest expanded, picked,
    // then the list refetched — must not disappear behind the button again.
    useEffect(() => {
      if (!theaterPerformanceId || showAllTheaterDays) return;
      const chosen = theaterPerformances.find((p) => p.id === theaterPerformanceId);
      if (!chosen) return;
      const isVisible = theaterDays
        .slice(0, THEATER_VISIBLE_DAYS)
        .some((day) => day.date === chosen.date);
      if (!isVisible) setShowAllTheaterDays(true);
    }, [theaterPerformanceId, theaterPerformances, theaterDays, showAllTheaterDays]);

    // Theater mode: a performance must be chosen and must still be in the
    // orderable list (the list only ever contains orderable performances).
    const validateTheaterPerformance = (id) => {
      const exists = Boolean(id) && theaterPerformances.some((p) => p.id === id);
      if (!exists) {
        setTheaterPerformanceError(true);
        toast.error(translations[language]?.order_information_theater_error);
        return false;
      }
      setTheaterPerformanceError(false);
      return true;
    };

    // Validate room number
    const validateRoomNumber = (value) => {
      // Wolt delivery replaces the room-number field with the address form
      if (restaurant?.EnableWoltDelivery) {
        setRoomNumberError(false);
        return true;
      }

      if (
        restaurant.AskTakeAwayRoom &&
        orderType === 'take-away' &&
        pickup === 'later' &&
        (!value || value === '')
      ) {
        setRoomNumberError(true);
        toast.error(translations[language]?.order_information_room_number_error);
        return false;
      }

      setRoomNumberError(false);
      return true;
    };

    // Validate all required fields based on order type
    const validateForm = () => {
      setFormTouched(true);
      let isValid = true;
      let firstErrorRef = null;

      if (orderType === 'dine-in') {
        if (isTheaterOrderNow) {
          // The performance stands in for the table number at a theater, so it
          // is what has to be answered before this order can be placed.
          const isPerformanceValid = validateTheaterPerformance(theaterPerformanceId);
          if (!isPerformanceValid && !firstErrorRef) {
            firstErrorRef = pickupDateTimeRef;
          }
          isValid = isValid && isPerformanceValid;
        } else {
          // For dine-in orders, validate table number
          const isTableValid = validateTableNumber(tableNumber);
          if (!isTableValid && !firstErrorRef) {
            firstErrorRef = tableNumberRef;
          }
          isValid = isValid && isTableValid;
        }
      } else if (orderType === 'take-away') {
        if (showTheaterPicker) {
          // Theater mode: the performance replaces the pickup date/time
          const isPerformanceValid = validateTheaterPerformance(theaterPerformanceId);
          if (!isPerformanceValid && !firstErrorRef) {
            firstErrorRef = pickupDateTimeRef;
          }
          isValid = isValid && isPerformanceValid;
        } else {
          // For take-away orders, validate pickup date and time
          const isDateValid = validatePickupDate(pickupDate);
          const isTimeValid = validatePickupTime(pickupTime);
          if ((!isDateValid || !isTimeValid) && !firstErrorRef) {
            firstErrorRef = pickupDateTimeRef;
          }
          isValid = isValid && isDateValid && isTimeValid;
        }

        if (restaurant.AskTakeAwayRoom) {
          const isRoomValid = validateRoomNumber(roomNumber);
          if (!isRoomValid && !firstErrorRef) {
            firstErrorRef = roomNumberRef;
          }
          isValid = isValid && isRoomValid;
        }
      } else if (isDelivery) {
        // Delivery for later: the same date and time checks as take-away. Wolt
        // would otherwise be asked to deliver into the past or a full slot.
        const isDateValid = validatePickupDate(pickupDate);
        const isTimeValid = validatePickupTime(pickupTime);
        if ((!isDateValid || !isTimeValid) && !firstErrorRef) {
          firstErrorRef = pickupDateTimeRef;
        }
        isValid = isValid && isDateValid && isTimeValid;
      }

      // Validate phone number when not a park table order
      const isPhoneValid = validatePhone(phoneNumber);
      if (!isPhoneValid && !firstErrorRef) {
        firstErrorRef = phoneRef;
      }

      if (!fromKiosk) {
        isValid = isValid && isPhoneValid;
      }

      // Scroll to first error field
      if (firstErrorRef?.current) {
        firstErrorRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }

      return isValid;
    };

    // Expose the validateForm function to parent component
    useImperativeHandle(ref, () => ({
      validateForm,
      scrollToPickupTime: () => {
        if (pickupDateTimeRef.current) {
          pickupDateTimeRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
      },
    }));

    // Custom handlers
    const handleTableNumberChange = (value) => {
      onTableNumberChange(value);
      if (formTouched) {
        validateTableNumber(value);
      }
    };

    const handlePhoneNumberChange = (value) => {
      onPhoneNumberChange(value);
      if (formTouched) {
        validatePhone(value);
      }
    };

    const handlePickupDateChange = (date) => {
      onPickupDateChange(date);
      if (formTouched) {
        validatePickupDate(date);
      }
    };

    const handlePickupTimeChange = (value) => {
      onPickupTimeChange(value);
      if (formTouched) {
        validatePickupTime(value);
      }
    };

    const handleRoomNumberChange = (value) => {
      onRoomNumberChange(value);
      if (formTouched) {
        validateRoomNumber(value);
      }
    };

    const handleTheaterPerformanceChange = (id) => {
      onTheaterPerformanceChange?.(id);
      setTheaterPerformanceError(false);
    };

    // Reset form validation errors when order type changes
    useEffect(() => {
      setTableNumberError(false);
      setPickupDateError(false);
      setPickupTimeError(false);
      setPickupTimeAvailabilityError(false);
      setRoomNumberError(false);
      setTheaterPerformanceError(false);
      setFormTouched(false);
    }, [orderType]);

    // Generate time options for the dropdown
    // Capacity-only check — separate from isTimeDisabled so we can label
    // capacity-disabled options differently from past-time / closed slots.
    // Returns false when capacitySlots is empty (capacity off / fetch failed),
    // so this stays a pure hook-in: nothing changes for non-capacity restaurants.
    const isCapacityFull = (timeStr) => {
      if (!capacitySlots.length) return false;
      const slot = capacitySlots.find((s) => s.slotStart <= timeStr && timeStr < s.slotEnd);
      return slot ? !slot.available : false;
    };

    const generateTimeOptions = () => {
      const times = [];
      for (let hour = 0; hour < 24; hour++) {
        for (let minute = 0; minute < 60; minute += pickupTimeInterval) {
          const formattedHour = hour.toString().padStart(2, '0');
          const formattedMinute = minute.toString().padStart(2, '0');
          times.push(`${formattedHour}:${formattedMinute}`);
        }
      }

      const output = [];
      const capacitySuffix = translations[language]?.picker_capacity_full_suffix || 'not available';

      times.forEach((timeStr) => {
        const capacityFull = isCapacityFull(timeStr);
        output.push({
          value: timeStr,
          label: capacityFull ? `${timeStr} — ${capacitySuffix}` : timeStr,
          disabled: isTimeDisabled(timeStr),
        });
      });

      return output;
    };

    const arraysEqual = (arr1, arr2) => {
      if (arr1.length !== arr2.length) return false;
      return arr1.every((val, index) => val === arr2[index]);
    };

    // Check if a time option should be disabled
    const isTimeDisabled = (timeStr) => {
      // If no date is selected, enable all times
      if (!pickupDate) return false;

      // Check if pickupDate is today
      const now = new Date();
      const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
      const selectedDate = new Date(
        pickupDate.getFullYear(),
        pickupDate.getMonth(),
        pickupDate.getDate(),
      );
      const isToday = selectedDate.getTime() === today.getTime();

      // Only apply current time restriction for today
      if (isToday) {
        // Calculate earliest available time based on lagTime
        // (Wolt delivery needs a 1 hour head start instead of the prep lag)
        const earliestAvailable = new Date(
          now.getTime() + (orderType === 'delivery' ? 60 : lagTime) * 60000,
        );
        earliestAvailable.setMinutes(
          Math.ceil(earliestAvailable.getMinutes() / pickupTimeInterval) * pickupTimeInterval,
          0,
          0,
        );

        const [hours, minutes] = timeStr.split(':').map(Number);
        const selectedTime = new Date();
        selectedTime.setHours(hours, minutes, 0, 0);

        // Disable times earlier than earliestAvailable
        if (selectedTime < earliestAvailable) {
          return true;
        }
      }

      // Capacity-management: disable times that fall inside a full slot.
      // Hook-gated on capacitySlots.length — non-capacity restaurants unaffected.
      if (isCapacityFull(timeStr)) return true;

      return disabledTimes.includes(timeStr);
    };

    const timeOptions = generateTimeOptions();
    const tomorrow = new Date();
    tomorrow.setDate(tomorrow.getDate() + 1);
    tomorrow.setHours(0, 0, 0, 0);

    // Error message style
    const errorMsgStyle = 'text-red-500 text-sm mt-1';
    const errorBorderStyle = '!border-red-500';

    return (
      <div className="order-information-container space-y-2">
        <div className="!px-3 !py-6 lg:!px-4 lg:!py-7 border-b border-[#f5f5f5]">
          <h2 className="text-base sm:text-lg md:text-xl font-bold">
            {translations[language]?.order_information_title}
          </h2>
          {fromHeyCharity && (
            <p className="text-sm lg:text-base text-[#707070]">
              {translations[language]?.heycharity_order_information_description}
            </p>
          )}
        </div>

        {/* Order Type Toggle — replaced by a plain title on the pre-order
            flow. Switching between "for now" and "for a performance" is a
            different order altogether (different timing, different gates), so
            it belongs on the choose-order step the guest came from, not in a
            toggle that would silently drop the chosen performance. */}
        {fromHeyCharity ? null : (
          <>
            {isTheaterMode ? (
              <div className="!px-3 !py-4 lg:!px-4 lg:!py-8 border-b border-[#f5f5f5]">
                <div className="w-full mx-auto max-w-sm flex items-center rounded-full p-1 bg-gray-100 h-[40px] sm:h-[50px]">
                  <p className="py-[6px] sm:py-[11px] px-2 w-full text-sm text-center rounded-full font-semibold  flex-1 bg-primary text-white cursor-auto">
                    {translations[language]?.order_type_theater || 'Forudbestilling'}
                  </p>
                </div>
              </div>
            ) : (
              restaurantId !== '682d79a98f4810001ac860e1' && (
                <div className="!px-3 !py-4 lg:!px-4 lg:!py-8 border-b border-[#f5f5f5]">
                  <div className="w-full mx-auto max-w-sm">
                    <ToggleNav
                      options={orderTypeOptions}
                      activeId={orderType}
                      onChange={onOrderTypeChange}
                      className="w-full"
                    />
                  </div>
                </div>
              )
            )}
          </>
        )}

        {/* Theater mode: the performance picker.
            For a PRE-ORDER it replaces the pickup now/later toggle and the
            date/time picker — the guest orders for a specific performance and
            the backend derives all timing from it.
            For an ORDER NOW at a theater it replaces the table number instead:
            a theater has no tables, so the performance is what tells the
            kitchen which house the guest is in. That order is immediate in
            every other respect. */}
        {showTheaterPicker && (
          <div
            ref={pickupDateTimeRef}
            className="!px-3 !py-4 lg:!px-4 lg:!py-8 border-b border-[#f5f5f5] space-y-2"
          >
            {/* Order-now must not read like a pre-order: with the same "choose
                the performance you are ordering for" wording, guests at
                Folketeatret bought for now believing they had pre-ordered for
                the interval. Here the performance only tells the counter which
                house the guest is in. */}
            <label className="block text-sm lg:text-base font-bold">
              {isTheaterOrderNow
                ? translations[language]?.order_information_theater_now_label
                : translations[language]?.order_information_theater_label}
            </label>
            <p className="text-xs lg:text-sm text-[#707070]">
              {isTheaterOrderNow
                ? translations[language]?.order_information_theater_now_description
                : translations[language]?.order_information_theater_description}
            </p>
            {theaterPerformances.length === 0 ? (
              <p className="text-xs lg:text-sm text-[#707070]">
                {translations[language]?.order_information_theater_none}
              </p>
            ) : (
              <div role="radiogroup">
                {/* Grouped by day: the date is said once as a heading and
                    each row underneath is just a time and a title. A venue
                    playing four shows on one December Saturday reads as one
                    date with four times, not four repeated dates. */}
                {visibleTheaterDays.map((day) => (
                  <div key={day.date} className="!mb-4 last:!mb-0">
                    <p
                      data-testid="theater-day-heading"
                      className="text-xs lg:text-sm font-bold uppercase tracking-wide text-[#707070] !mb-2"
                    >
                      {formatTheaterDayHeading(day.date, language, {
                        today: translations[language]?.order_information_theater_today,
                        tomorrow: translations[language]?.order_information_theater_tomorrow,
                      })}
                    </p>
                    <div className="space-y-2">
                      {day.performances.map((performance) => (
                        <label
                          key={performance.id}
                          className={`flex items-center gap-3 !px-4 !py-3 border rounded-xl cursor-pointer transition-colors ${
                            theaterPerformanceId === performance.id
                              ? 'border-gray-900 bg-[#f5f5f5]'
                              : theaterPerformanceError
                                ? errorBorderStyle
                                : 'border-[#e2e8f0]'
                          }`}
                        >
                          <input
                            type="radio"
                            name="theater-performance"
                            className="accent-[#222]"
                            checked={theaterPerformanceId === performance.id}
                            onChange={() => handleTheaterPerformanceChange(performance.id)}
                          />
                          {/* Fixed time column so titles line up down the
                              day. tabular-nums keeps digits equal-width. */}
                          {(() => {
                            const parts = formatTheaterPerformanceParts(performance, language);
                            return (
                              <span className="text-sm lg:text-base grid grid-cols-[3.25rem_auto] items-baseline w-full tabular-nums">
                                <span>{parts.time}</span>
                                <span className="truncate">{parts.name}</span>
                              </span>
                            );
                          })()}
                        </label>
                      ))}
                    </div>
                  </div>
                ))}

                {/* The rest of the season — never truncated, just folded. */}
                {hiddenTheaterDayCount > 0 && (
                  <button
                    type="button"
                    onClick={() => setShowAllTheaterDays(true)}
                    className="w-full !mt-2 !px-4 !py-3 border border-[#e2e8f0] rounded-xl text-sm lg:text-base font-bold"
                  >
                    {`${translations[language]?.order_information_theater_more_dates} (${hiddenTheaterDayCount})`}
                  </button>
                )}
                {showAllTheaterDays && theaterDays.length > THEATER_VISIBLE_DAYS && (
                  <button
                    type="button"
                    onClick={() => setShowAllTheaterDays(false)}
                    className="w-full !mt-2 !px-4 !py-3 border border-[#e2e8f0] rounded-xl text-sm lg:text-base font-bold"
                  >
                    {translations[language]?.order_information_theater_fewer_dates}
                  </button>
                )}
              </div>
            )}
            {theaterPerformanceError && (
              <p className={errorMsgStyle}>
                {translations[language]?.order_information_theater_error}
              </p>
            )}
          </div>
        )}

        {/* Table Number — dine-in, except at a theater, where the performance
            above stands in for it. */}
        {orderType === 'dine-in' && !isTheaterOrderNow && (
          <>
            {restaurantId === CINEMA_ROW_SEAT_RESTAURANT_ID ? (
              <CinemaRowSeatDropdowns
                tableNumbers={tableNumbers}
                value={tableNumber}
                onChange={handleTableNumberChange}
                hasError={tableNumberError}
              />
            ) : (
              <div
                ref={tableNumberRef}
                className="!px-3 !py-4 lg:!px-4 lg:!py-8 border-b border-[#f5f5f5] flex flex-col sm:flex-row sm:items-center"
              >
                <div className="flex-1 mb-2 sm:!mb-0">
                  <label className="block text-sm lg:text-base font-bold">
                    {translations[language]?.order_information_table_number_label}
                  </label>
                  <p className="text-xs lg:text-sm text-[#707070]">
                    {translations[language]?.order_information_table_number_description}
                  </p>
                </div>
                <div className="flex-1">
                  <GenericSelect
                    placeholder={translations[language]?.order_information_table_number_placeholder}
                    options={tableNumbers}
                    value={tableNumber}
                    onChange={(selectedOption) => {
                      handleTableNumberChange(selectedOption);
                    }}
                    hasError={tableNumberError}
                  />
                  {tableNumberError && (
                    <p className={errorMsgStyle}>
                      {translations[language]?.order_information_table_number_error}
                    </p>
                  )}
                </div>
              </div>
            )}
          </>
        )}

        {/* Pickup Time (visible for take-away & delivery) */}
        {(orderType === 'take-away' || orderType === 'delivery') && (
          <>
            {!showTheaterPicker && restaurant.allowAdvanceOrder && (
              <div className="!px-3 !py-4 lg:!px-4 lg:!py-8 border-b border-[#f5f5f5] flex flex-col sm:flex-row sm:items-center">
                <div className="flex-1 mb-2 sm:!mb-0">
                  <label className="block text-sm lg:text-base font-bold">
                    {orderType === 'delivery'
                      ? translations[language]?.order_information_deliver_label
                      : translations[language]?.order_information_pickup_label}
                  </label>
                  <p className="text-xs lg:text-sm text-[#707070]">
                    {orderType === 'delivery'
                      ? translations[language]?.order_information_deliver_description
                      : translations[language]?.order_information_pickup_description}
                  </p>
                </div>
                <div className="flex-1">
                  <ToggleNav
                    options={
                      pickupOptions?.map((option) => {
                        return {
                          ...option,
                          label:
                            orderType === 'delivery' && option.id === 'now'
                              ? translations[language]?.deliver_now
                              : orderType === 'delivery' && option.id === 'later'
                                ? translations[language]?.deliver_later
                                : option.label,
                        };
                      }) || []
                    }
                    activeId={pickup}
                    onChange={onPickupChange}
                    className="w-full"
                  />
                </div>
              </div>
            )}

            {!showTheaterPicker && pickup === 'later' && (
              <>
                <div
                  ref={pickupDateTimeRef}
                  className="!px-3 !py-4 lg:!px-4 lg:!py-8 border-b border-[#f5f5f5] space-y-2"
                >
                  <label className="block text-sm lg:text-base font-bold">
                    {orderType === 'delivery'
                      ? translations[language]?.order_information_deliver_later_label
                      : translations[language]?.order_information_pickup_later_label}
                  </label>
                  <div
                    className={`flex space-x-4${
                      restaurant.allowAdvanceOrder ? '' : ' pointer-events-none opacity-60'
                    }`}
                  >
                    <div className="flex-1">
                      <div className="relative inline-block w-full">
                        <DatePicker
                          selected={pickupDate}
                          onChange={(date) => handlePickupDateChange(date)}
                          dateFormat="MMMM d, yyyy"
                          minDate={isPickupForTomorrow ? tomorrow : new Date()}
                          placeholderText={
                            orderType === 'delivery'
                              ? translations[language]
                                  ?.order_information_deliver_later_date_placeholder
                              : translations[language]
                                  ?.order_information_pickup_later_date_placeholder
                          }
                          className={`custom-datepicker border-[#e2e8f0] focus:border-gray-900 ${
                            pickupDateError ? errorBorderStyle : ''
                          }`}
                        />
                        <div className="absolute top-1/2 -translate-y-1/2 right-0 flex items-center pr-3 pointer-events-none z-10">
                          <i className="fa fa-calendar-alt text-gray-700" />
                        </div>
                      </div>
                      {pickupDateError && (
                        <p className={errorMsgStyle}>
                          {orderType === 'delivery'
                            ? translations[language]?.order_information_deliver_later_date_error
                            : translations[language]?.order_information_pickup_later_date_error}
                        </p>
                      )}
                    </div>
                    <div className="flex-1">
                      <div className="relative inline-block w-full">
                        <TimePicker
                          className={`${
                            pickupTimeError ||
                            pickupTimeAvailabilityError ||
                            pickupTimeCapacityError
                              ? errorBorderStyle
                              : ''
                          }`}
                          value={pickupTime}
                          onChange={handlePickupTimeChange}
                          selectedDate={pickupDate}
                          disabledTimes={disabledTimes}
                          timeOptions={timeOptions}
                        />
                      </div>
                      {pickupTimeError && (
                        <p className={errorMsgStyle}>
                          {orderType === 'delivery'
                            ? translations[language]?.order_information_deliver_later_time_error
                            : translations[language]?.order_information_pickup_later_time_error}
                        </p>
                      )}
                      {pickupTimeAvailabilityError && (
                        <p className={errorMsgStyle}>
                          {orderType === 'delivery'
                            ? translations[language]
                                ?.order_information_deliver_later_time_unavailable
                            : translations[language]
                                ?.order_information_pickup_later_time_unavailable}
                        </p>
                      )}
                      {pickupTimeCapacityError && (
                        <p className={errorMsgStyle}>
                          {translations[language]?.order_information_pickup_time_capacity_full ||
                            'The selected time is no longer available — please pick another.'}
                        </p>
                      )}
                    </div>
                  </div>
                </div>

                {restaurant.AskTakeAwayRoom && !restaurant?.EnableWoltDelivery && (
                  <>
                    <div
                      ref={roomNumberRef}
                      className="!px-3 !py-4 lg:!px-4 lg:!py-8 border-b border-[#f5f5f5] flex flex-col sm:flex-row sm:items-center"
                    >
                      <div className="flex-1 mb-2 sm:!mb-0">
                        <label className="block text-sm lg:text-base font-bold">
                          {translations[language]?.order_information_room_number_label}
                        </label>
                        <p className="text-xs lg:text-sm text-[#707070]">
                          {translations[language]?.order_information_room_number_description}
                        </p>
                      </div>
                      <div className="flex-1">
                        <input
                          type="number"
                          className={`input-discount text-sm w-full px-4 py-[14px] border focus:border-gray-900 rounded-full ${
                            roomNumberError ? errorBorderStyle : ''
                          }`}
                          placeholder={
                            translations[language]?.order_information_room_number_placeholder
                          }
                          value={roomNumber}
                          onChange={(e) => {
                            minMaxInputNumber(e);
                            handleRoomNumberChange(e.target.value);
                          }}
                          onKeyDown={(e) =>
                            ['e', 'E', '+', '-'].includes(e.key) && e.preventDefault()
                          }
                          min="0"
                        />
                        {roomNumberError && (
                          <p className={errorMsgStyle}>
                            {translations[language]?.order_information_room_number_error}
                          </p>
                        )}
                      </div>
                    </div>
                  </>
                )}
              </>
            )}

            {/* Wolt Delivery */}
            {restaurant?.EnableWoltDelivery && (
              <>
                {/* <div className="!px-3 !py-4 lg:!px-4 lg:!py-8 border-b border-[#f5f5f5] flex flex-col sm:flex-row sm:items-center">
                      <div className="flex-1 mb-2 sm:!mb-0">
                        <label className="block text-sm lg:text-base font-bold">
                          {translations[language]?.order_information_delivery_label}
                        </label>
                        <p className="text-xs lg:text-sm text-[#707070]">
                          {translations[language]?.order_information_delivery_description}
                        </p>
                      </div>
                      <div className="flex-1">
                        <ToggleNav
                          options={woltDeliveryOptions}
                          activeId={isWoltDeliveryEnabled ? 'yes' : 'no'}
                          onChange={(id) => onWoltDeliveryFeeToggle(id === 'yes')}
                        />
                      </div>
                    </div> */}

                {/* Minimum order value for delivery. Shown the moment delivery
                    is picked rather than on submit: it is the cart that has to
                    change, and the cart is on the same screen. Above the address
                    because filling one in cannot fix it. */}
                {orderType === 'delivery' && !woltMinOrder.met && (
                  <div className="!px-3 !py-2 lg:!px-4 lg:!py-4">
                    <p className="text-sm text-red-500">{woltMinOrder.message}</p>
                  </div>
                )}

                {orderType === 'delivery' && (
                  <AddressInformation
                    ref={addressRef}
                    address={address}
                    setAddress={setAddress}
                    onCheckWoltDeliveryBlur={onCheckWoltDeliveryBlur}
                    onAddressEdit={onWoltAddressEdit}
                    deliveryComment={deliveryComment}
                    onDeliveryCommentChange={onDeliveryCommentChange}
                    restaurant={restaurant}
                  />
                )}
              </>
            )}

            {restaurant.BagTakeAway && (
              <div className="!px-3 !py-4 lg:!px-4 lg:!py-8 border-b border-[#f5f5f5] flex flex-col sm:flex-row sm:items-center">
                <div className="flex-1 mb-2 sm:!mb-0">
                  <label className="block text-sm lg:text-base font-bold">
                    {translations[language]?.order_information_bag_label}
                  </label>
                  <p className="text-xs lg:text-sm text-[#707070]">
                    {translations[language]?.order_information_bag_description}
                  </p>
                </div>
                <div className="flex-1">
                  <ToggleNav
                    options={bagOptions}
                    activeId={hasBag ? 'yes' : 'no'}
                    onChange={(id) => onBagToggle(id === 'yes')}
                    disabled={orderType === 'delivery'}
                  />
                </div>
              </div>
            )}
          </>
        )}

        {/* Tips */}
        {fromHeyCharity
          ? null
          : restaurantTips?.length > 0 &&
            orderType === 'dine-in' && (
              <div className="!px-3 !py-4 lg:!px-4 lg:!py-8 border-b border-[#f5f5f5] flex flex-col sm:flex-row sm:items-center">
                <div className="flex-1 mb-2 sm:!mb-0">
                  <label className="block text-sm lg:text-base font-bold">
                    {translations[language]?.order_information_tip_label}
                  </label>
                  <p className="text-xs lg:text-sm text-[#707070]">
                    {translations[language]?.order_information_tip_description}
                  </p>
                </div>
                <div className="flex-1">
                  <GenericSelect
                    placeholder={translations[language]?.order_information_tip_placeholder}
                    options={[
                      { value: '0', label: translations[language]?.order_information_no_tip_label },
                      ...restaurantTips.map((item) => ({
                        value: item?._id || item.tipPercentage?.toString(),
                        label: `${item.tipPercentage}% + ${currency} ${(
                          item.tipPercentage *
                          0.01 *
                          baseAmount
                        ).toFixed(2)}`,
                        percentage: (item.tipPercentage * 0.01).toString(),
                        ...item,
                      })),
                    ]}
                    value={
                      tipPercentage === '0'
                        ? {
                            value: '0',
                            label: translations[language]?.order_information_no_tip_label,
                          }
                        : restaurantTips
                            .map((item) => ({
                              value: item?._id || item.tipPercentage?.toString(),
                              label: `${item.tipPercentage}% + ${currency} ${(
                                item.tipPercentage *
                                0.01 *
                                baseAmount
                              ).toFixed(2)}`,
                              percentage: (item.tipPercentage * 0.01).toString(),
                              ...item,
                            }))
                            .find((option) => option.value === (tipId || tipPercentage))
                    }
                    onChange={(selectedOption) => {
                      onTipPercentageChange(selectedOption);
                    }}
                  />
                </div>
              </div>
            )}

        {/* Mobile */}
        {!isGuest && (
          <div
            ref={phoneRef}
            className="!px-3 !py-4 lg:!px-4 lg:!py-8 border-b border-[#f5f5f5] flex flex-col sm:flex-row sm:items-center"
          >
            <div className="flex-1 mb-2 sm:!mb-0">
              <label className="block text-sm lg:text-base font-bold">
                {translations[language]?.order_information_mobile_number_label}
              </label>
              <p className="text-xs lg:text-sm text-[#707070]">
                {translations[language]?.order_information_mobile_number_description}
              </p>
            </div>
            <div className="flex-1 flex flex-col items-start">
              <PhoneInput
                country={languageToPhoneCountry(
                  language,
                  restaurant?.currency?.code === 'NOK' ? 'no' : 'dk',
                )}
                value={phoneNumber}
                onChange={handlePhoneNumberChange}
                tabIndex={'4'}
                className={`phoneinput ${phoneError ? 'phone-error' : ''}`}
                containerClass={phoneError ? 'phone-container-error' : ''}
                inputClass={phoneError ? 'phone-input-error' : ''}
              />
              {phoneError && (
                <p className={errorMsgStyle}>
                  {translations[language]?.order_information_mobile_number_error}
                </p>
              )}
            </div>
          </div>
        )}

        {/* Comments */}
        <div className="!px-3 !py-4 lg:!px-4 lg:!py-8 border-b border-[#f5f5f5] space-y-4">
          <label className="block text-sm lg:text-base font-bold">
            {translations[language]?.order_information_comments_label}
          </label>
          <textarea
            className="w-full p-4 border border-[#f4f4f4] rounded-xl h-[150px] resize-none text-sm"
            placeholder={`${translations[language]?.comments_placeholder}...`}
            value={comments}
            onChange={(e) => onCommentsChange(e.target.value)}
          ></textarea>
        </div>

        {/* Discount Code. What used to live here was dead UI: Apply only wrote to
            the console, and any non-empty string produced a hardcoded 10% that
            YourItems destructured and never rendered. This is the real
            four-state block. RestaurantKiosk/index.js imports this same
            component, so the kiosk gets it at the same time. */}
        <DiscountCodeBlock
          code={discountCode}
          onCodeChange={onDiscountCodeChange}
          onApply={onApplyDiscount}
          onRemove={onRemoveDiscount}
          currency={currency}
          locked={discountLocked}
          status={discountStatus}
          amount={discountAmount}
          error={discountError}
        />
      </div>
    );
  },
);

export default OrderInformation;
